import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { User } from './user';
export { User };
import { userSchema } from '@schemas/business-objects/user';
import { GroupMatchFormArray } from './group-match-form-group';
import { MatchFormArray } from './match-form-group';
import { RoleAssignmentFormArray } from './role-assignment-form-group';

export class UserFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    email: SchemaFormControl;
    lastSeen: SchemaFormControl;
    roleAssignments: RoleAssignmentFormArray;
    matchesRun: MatchFormArray;
    groupMatchesRun: GroupMatchFormArray;
  };

  declare value: Partial<User>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(userSchema, User, data, options);
  }

  get object(): User {
    return new User(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'roleAssignments':
        return new RoleAssignmentFormArray();
      case 'matchesRun':
        return new MatchFormArray();
      case 'groupMatchesRun':
        return new GroupMatchFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<User[]> {
    return User.find(filter);
  }

  static async findOne(filter?: any): Promise<User | null> {
    return User.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<User> {
    return User.findById(id, filter);
  }

  static async create(data: Partial<User>): Promise<User> {
    return User.create(data as any);
  }

  static async updateById(id: number, updates: Partial<User>): Promise<User> {
    return User.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return User.deleteById(id);
  }

  static async currentUser(filter?: any): Promise<UserFormGroup | null> {
    const result = await User.currentUser(filter);
    if (!result) return null;
    const fg = new UserFormGroup();
    fg._populate(result);
    return fg;
  }
}

export class UserFormArray extends PersistedFormArray {
  declare controls: UserFormGroup[];

  readonly entityName = 'User' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(userSchema, User, options);
  }

  protected override createItemGroup() {
    return new UserFormGroup();
  }

  get array(): User[] {
    return this.controls.map((group: UserFormGroup) => group.object);
  }
}

export class UserPersistedArray extends PersistedArray<User> {
  readonly entityName = 'User' as const;

  constructor(options?: PersistedArrayOptions) {
    super(User, options);
  }
}
